import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {PageTypeOption} from '@models/sidebar/response/page-type-option';
import {PageItem} from '@models/sidebar/page-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {ExternalUrlPage} from '@models/sidebar/response/external-url-page';
import {ExternalUrlService} from '@services/left-menu/page/external-url-page/external-url.service';
import {EXTERNAL_URL_REGEX, URL_REGEX} from '@constants/reg-exp';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {DesignService} from '@services/left-menu/design/design.service';
import {MenuItemIcon} from '@models/sidebar/menu-item-icon';
import {FramePageService} from '@services/left-menu/page/frame-page/frame-page.service';
import {InlineFramePage} from '@models/sidebar/response/inline-frame-page';
import {Subject, Subscription} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {
  TemplatePagesService
} from '@services/left-menu/page/template-user-defined-page/template-pages.service';

@Component({
    selector: 'scitis-edit-page-inline-form',
    templateUrl: './edit-page-inline-form.component.html',
    styleUrls: ['./edit-page-inline-form.component.scss'],
    standalone: false
})
export class EditPageInlineFormComponent implements OnInit, OnDestroy {

  @Input()
  machineId: string | null = '';

  @Input()
  baseMenuItems: PageItem[] = [];

  @Input()
  userMenuItems: PageItem[] = [];

  @Input()
  pageTypeOptions: PageTypeOption[] = [];

  @Input()
  pageItem: PageItem | undefined;

  @Input()
  allPagesInsideNode: PageItem[] = [];

  @Input()
  pageItemsToBeDeleted: PageItem[] = [];

  @Output()
  newPageSave: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  newPageCancel: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  propertiesFallback: EventEmitter<Object> = new EventEmitter<Object>();

  addPageForm: UntypedFormGroup = this.fb.group({});
  pageNameIsNotUniqueOnMachine: boolean = false;
  templatePageNameIsNotUniqueAcrossAllMachines: boolean = false;
  showUrlField: boolean = false;
  loadUrl: boolean = false;
  enteredName: string = '';
  iconClass: string = '';
  isNewPage: boolean = false;
  submitted: boolean = false;
  subscription: Subscription = new Subscription();
  propertiesFallbackObject = {
    name: '',
    url: ''
  };
  framePageHelp: string = '';
  MenuItemType = MenuItemType;
  checkTemplatePageName: boolean = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private fb: UntypedFormBuilder, private externalUrlService: ExternalUrlService,
              private leftMenuService: LeftMenuService, private designService: DesignService,
              private framePageService: FramePageService,
              private templatePagesService: TemplatePagesService,
              private translate: TranslateService) {
    this.framePageHelp = this.translate.instant('It is possible that this website does not allow to be embedded. In this case, it cannot be properly displayed.');
  }

  get f() {
    return this.addPageForm.controls;
  }

  get isSavePageButtonDisabled(): boolean {
    return this.submitted ? this.addPageForm.invalid : this.submitted && this.addPageForm.invalid;
  };

  get isPageTypeDisabled(): boolean {
    return !!this.pageItem?.type;
  }

  get isFramePage(): boolean {
    return this.pageItem?.type === MenuItemType.FRAME_PAGE;
  }

  public isDisabledOption(option: PageTypeOption): boolean {
    return this.baseMenuItems.some(item => item.type === MenuItemType.MACHINE_GROUP_STATUS_PAGE) && option.value === MenuItemType.MACHINE_GROUP_STATUS_PAGE;
  };

  ngOnInit() {
    this.enteredName = this.translate.instant('New page');
    this.loadProperties(this.pageItem);
    this.generateAddPageForm(this.pageItem);
    if (!this.pageItem) {
      this.isNewPage = true;
      if (this.pageTypeOptions.length === 1) {
        this.f?.type.setValue(this.pageTypeOptions[0].value);
      }
    }
    this.iconClass = MenuItemIcon.ROOT_FOLDER;
    this.leftMenuService.broadcastErrorUpdatePageChange('');
    this.subscription.unsubscribe();
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getPattern() {
    return this.pageItem?.type === MenuItemType.FRAME_PAGE ? URL_REGEX : EXTERNAL_URL_REGEX;
  }


  onSaveNewPage(): void {
    const currentName = this.pageItem?.name;
    const currentURL = this.pageItem?.url;
    this.submitted = true;
    if (this.addPageForm.valid) {
      if (!this.pageItem) {
        this.pageItem = new PageItem();
      }
      this.pageItem.name = this.f.name.value.trim();
      this.pageItem.type = this.f.type.value;
      if (this.pageItem.type === MenuItemType.EXTERNAL_URL || this.pageItem.type === MenuItemType.FRAME_PAGE) {
        this.pageItem.url = this.f.url.value;
        this.propertiesFallbackObject.url = currentURL ? currentURL : '';
      }
      this.propertiesFallbackObject.name = currentName ? currentName : '';
      this.propertiesFallback.emit(this.propertiesFallbackObject);
      this.newPageSave.emit(this.pageItem);
      this.subscribeToItemNameChanges();
    }

  }

  onCancelSavePage(): void {
    this.newPageCancel.emit();
  }

  onTypeChange(type: MenuItemType) {
    if ((type === MenuItemType.TEMPLATE_USER_DEFINED_PAGE || type === MenuItemType.TEMPLATE_STATUS_EVENT_PAGE) && this.f.name.value) {
      this.isTemplatePageNameUnique(this.f.name.value);
    }

    if (type === MenuItemType.EXTERNAL_URL || type === MenuItemType.FRAME_PAGE) {
      this.addPageForm.get('url')?.setValidators([Validators.required, Validators.pattern(this.getPattern())]);
      this.showUrlField = true;
    } else {
      this.addPageForm.get('url')?.setValidators(null);
      this.showUrlField = false;
    }
    this.addPageForm.get('url')?.updateValueAndValidity();
    this.iconClass = this.designService.getIcon(type).toString();
  }

  onUpdateName(): void {
    this.enteredName = this.f.name.value.trim();
    const valueFromInputField = this.f.name.value;
    const tabValue = this.pageItem?.name;
    const arraysToVerify = [...this.baseMenuItems, ...this.userMenuItems, ...this.allPagesInsideNode, ...this.pageItemsToBeDeleted];

    this.pageNameIsNotUniqueOnMachine = valueFromInputField && this.baseMenuItems
      ? this.leftMenuService.checkIfElementExistsByName(valueFromInputField, arraysToVerify, tabValue)
      : false;

    if (this.f.type.value === MenuItemType.TEMPLATE_USER_DEFINED_PAGE || this.f.type.value === MenuItemType.TEMPLATE_STATUS_EVENT_PAGE) {
      this.isTemplatePageNameUnique(valueFromInputField);
    }
  }

  loadProperties(pageItem: PageItem | undefined) {
    if (!pageItem?.url && pageItem?.parentId && (pageItem?.type === MenuItemType.EXTERNAL_URL || pageItem?.type === MenuItemType.FRAME_PAGE)) {
      this.loadUrl = true;
      const isParentMachineGroup = this.pageItem?.menuItemParent?.type === MenuItemType.MACHINE_GROUP;
      if (pageItem?.type === MenuItemType.EXTERNAL_URL) {
        this.getExternalUrlPage(pageItem, isParentMachineGroup);
      }
      if (pageItem?.type === MenuItemType.FRAME_PAGE) {
        this.getInlineFramePage(pageItem, isParentMachineGroup);
      }
    }
  }

  public getTitle(pageTypeOption: PageTypeOption): string {
    return this.isDisabledOption(pageTypeOption) ? this.translate.instant('Status page was already created') : '';
  }

  private getInlineFramePage(pageItem: PageItem, isParentMachineGroup: boolean) {
    if (pageItem?.parentId && pageItem.pageId) {
      this.framePageService.getFramePage(pageItem?.parentId, isParentMachineGroup, pageItem.pageId).subscribe((result: InlineFramePage) => {
        pageItem.url = result.url;
        this.addPageForm.get('url')?.setValue(result.url);
        this.loadUrl = false;
      }, (error) => {
        console.log(error);
        this.loadUrl = false;
      });
    }
  }

  private getExternalUrlPage(pageItem: PageItem, isParentMachineGroup: boolean) {
    if (pageItem?.parentId && pageItem?.pageId) {
      this.externalUrlService.getExternalUrl(pageItem?.parentId, isParentMachineGroup, pageItem?.pageId).subscribe((result: ExternalUrlPage) => {
        pageItem.url = result.url;
        this.addPageForm.get('url')?.setValue(result.url);
        this.loadUrl = false;
      }, (error) => {
        console.log(error);
        this.loadUrl = false;
      });
    }
  }

  private subscribeToItemNameChanges() {
    this.subscription = this.leftMenuService.errorUpdatePageChange.pipe(take(2))
    .subscribe((prevProperties: any) => {
      if (this.pageItem && prevProperties) {
        this.pageItem.name = prevProperties.name;
        if (this.pageItem.type === MenuItemType.EXTERNAL_URL || this.pageItem.type === MenuItemType.FRAME_PAGE) {
          this.pageItem.url = prevProperties.url;
        }
      }

    });
  }

  private generateAddPageForm(pageItem: PageItem | undefined): void {
    this.showUrlField = pageItem?.type === MenuItemType.EXTERNAL_URL || pageItem?.type === MenuItemType.FRAME_PAGE;

    this.addPageForm = this.fb.group({
      name: [
        pageItem?.name ? pageItem?.name : '',
        [Validators.required]
      ],
      type: [
        {value: pageItem?.type ? pageItem?.type : '', disabled: this.isPageTypeDisabled},
        [Validators.required]
      ],
      url: [
        pageItem?.url ? pageItem?.url : '',
        this.showUrlField ? [Validators.required, Validators.pattern(this.getPattern())] : []
      ]
    });
  }

  private isTemplatePageNameUnique(valueFromInputField: string) {
    if (!this.machineId) {
      return;
    }
    this.checkTemplatePageName = true;
    this.templatePagesService.isPageNameAlreadyUsed(this.machineId, valueFromInputField)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.templatePageNameIsNotUniqueAcrossAllMachines = result[0];
        this.checkTemplatePageName = false;
      }, () => {
        console.error('Error getting the status if the page name is already used.');
        this.checkTemplatePageName = false;
      });
  }
}
